import { Component } from '@theme/component';
import { getWishlistItems, toggleWishlist, FcWishlistChangeEvent } from '@theme/fc-wishlist-api';
import { formatMoney } from '@theme/money-formatting';

/**
 * Wishlist page controller. Storage only ever holds {handle, variantId}
 * pairs — this fetches live product/variant data from Shopify's own
 * /products/{handle}.js AJAX endpoint at render time, so price/stock is
 * always current instead of a stale snapshot from add-time.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} loading
 * @property {HTMLElement} empty
 * @property {HTMLElement} error
 * @property {HTMLUListElement} list
 * @extends {Component<Refs>}
 */
class FcWishlistPage extends Component {
  requiredRefs = ['loading', 'empty', 'error', 'list'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(FcWishlistChangeEvent.eventName, this.#onWishlistChange);
    this.#loadItems();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FcWishlistChangeEvent.eventName, this.#onWishlistChange);
  }

  #onWishlistChange = () => {
    this.#loadItems();
  };

  async #loadItems() {
    this.refs.loading.hidden = false;
    this.refs.empty.hidden = true;
    this.refs.error.hidden = true;
    this.refs.list.hidden = true;

    /** @type {{handle: string, variantId: number|null}[]} */
    let items;
    try {
      items = await getWishlistItems();
    } catch (err) {
      console.error(err);
      this.refs.loading.hidden = true;
      this.refs.error.hidden = false;
      this.refs.error.textContent = this.dataset.errorText;
      return;
    }

    if (!items.length) {
      this.refs.loading.hidden = true;
      this.refs.empty.hidden = false;
      return;
    }

    const entries = (
      await Promise.all(
        items.map((item) =>
          fetch(`/products/${item.handle}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => (product ? { item, product } : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);

    this.refs.loading.hidden = true;

    if (!entries.length) {
      this.refs.empty.hidden = false;
      return;
    }

    this.#renderCards(entries);
    this.refs.list.hidden = false;
  }

  /** @param {{item: {handle: string, variantId: number|null}, product: object}[]} entries */
  #renderCards(entries) {
    this.refs.list.replaceChildren(...entries.map(({ item, product }) => this.#buildCard(item, product)));
  }

  /**
   * @param {{handle: string, variantId: number|null}} item
   * @param {object} product
   */
  #buildCard(item, product) {
    const variant = product.variants.find((v) => v.id === item.variantId) || product.variants[0];
    const image = variant.featured_image?.src || product.featured_image || product.images[0] || '';
    const variantUrl = `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${variant.id}`;

    // The quick-add dialog shell is only rendered site-wide when the merchant
    // has quick add enabled in theme settings (layout/theme.liquid) — when
    // it's off, #quick-add-dialog doesn't exist and the trigger button would
    // silently no-op, so fall back to a plain link to the product page.
    const quickViewMarkup = document.getElementById('quick-add-dialog')
      ? `<product-card style="display:contents">
          <a ref="productCardLink" href="${variantUrl}" hidden></a>
          <quick-add-component data-product-title="${escapeHtml(product.title)}">
            <button type="button" class="button button-small" on:click="quick-add-component/handleClick">
              ${this.dataset.labelQuickView}
            </button>
          </quick-add-component>
        </product-card>`
      : `<a href="${variantUrl}" class="button button-small">${this.dataset.labelQuickView}</a>`;

    const li = document.createElement('li');
    li.className = 'fc-wishlist-page__item';
    li.innerHTML = `
      <a href="${product.url}" class="fc-wishlist-page__item-image">
        <img src="${image}" alt="" loading="lazy">
      </a>
      <div class="fc-wishlist-page__item-info">
        <a href="${product.url}" class="fc-wishlist-page__item-title">${escapeHtml(product.title)}</a>
        <p class="fc-wishlist-page__item-price">${this.#money(variant.price)}</p>
      </div>
      <div class="fc-wishlist-page__item-actions">${quickViewMarkup}</div>
      <button type="button" class="fc-wishlist-page__remove" data-action="remove" aria-label="${this.dataset.labelRemove}">
        &times;
      </button>
    `;

    li.querySelector('[data-action="remove"]')?.addEventListener('click', () =>
      toggleWishlist(item.handle).catch((err) => console.error(err))
    );

    return li;
  }

  /**
   * Formats cents from /products/{handle}.js with the shop's own money
   * format, the same pairing snippets/price-filter.liquid uses. Under
   * Shopify Markets, Shopify.currency.active is the visitor's presentment
   * currency, so no conversion is needed here.
   *
   * @param {number} cents
   */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', window.Shopify?.currency?.active || 'USD');
  }
}

/** @param {unknown} value */
function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
  );
}

if (!customElements.get('fc-wishlist-page')) customElements.define('fc-wishlist-page', FcWishlistPage);
